import { Clock, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import type { ReportStatus } from './types'

interface ReportProcessingStateProps {
  status: ReportStatus
  onRefresh?: () => void
}

export function ReportProcessingState({
  status,
  onRefresh,
}: ReportProcessingStateProps) {
  if (status === 'completed') {
    return null
  }

  const stateConfig = {
    pending: {
      icon: Loader2,
      iconClass: 'text-teal-500 dark:text-teal-400 animate-spin',
      bgGradient:
        'from-teal-50 via-indigo-50/50 to-teal-50 dark:from-teal-950/30 dark:via-indigo-950/20 dark:to-teal-950/30',
      borderColor: 'border-teal-200/60 dark:border-teal-800/40',
      title: 'Generating Report',
      description:
        'The AI is currently analyzing your items and generating insights. This typically takes 30-60 seconds.',
      hint: 'You can leave this page and come back later, or refresh to check the status.',
      pulseRing: true,
    },
    processing: {
      icon: Loader2,
      iconClass: 'text-teal-500 dark:text-teal-400 animate-spin',
      bgGradient:
        'from-teal-50 via-indigo-50/50 to-teal-50 dark:from-teal-950/30 dark:via-indigo-950/20 dark:to-teal-950/30',
      borderColor: 'border-teal-200/60 dark:border-teal-800/40',
      title: 'Generating Report',
      description:
        'The AI is currently analyzing your items and generating insights. This typically takes 30-60 seconds.',
      hint: 'Reload this page or come back in a few minutes to see your completed report.',
      pulseRing: true,
    },
    failed: {
      icon: AlertCircle,
      iconClass: 'text-red-500 dark:text-red-400',
      bgGradient:
        'from-red-50 via-amber-50/50 to-red-50 dark:from-red-950/30 dark:via-amber-950/20 dark:to-red-950/30',
      borderColor: 'border-red-200/60 dark:border-red-800/40',
      title: 'Report Generation Failed',
      description:
        'Something went wrong while generating this report. This could be due to a temporary issue with the AI service.',
      hint: 'You can try regenerating the report using the button above, or contact support if the issue persists.',
      pulseRing: false,
    },
  }

  const config = stateConfig[status as keyof typeof stateConfig]

  if (!config) return null

  const Icon = config.icon

  return (
    <div className="py-12 px-6">
      <div
        className={`
        relative overflow-hidden rounded-2xl border ${config.borderColor}
        bg-gradient-to-br ${config.bgGradient}
        p-8 sm:p-12
      `}
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="grid"
                width="32"
                height="32"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="1" cy="1" r="1" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative flex flex-col items-center text-center">
          {/* Icon with optional pulse ring */}
          <div className="relative mb-6">
            {config.pulseRing && (
              <>
                <div className="absolute inset-0 animate-ping rounded-full bg-teal-400/20 dark:bg-teal-500/20" />
                <div className="absolute -inset-2 animate-pulse rounded-full bg-teal-400/10 dark:bg-teal-500/10" />
              </>
            )}
            {config.pulseRing ? (
              <div className="relative flex h-16 w-16 items-center justify-center">
                <Icon className={`h-10 w-10 ${config.iconClass}`} />
              </div>
            ) : (
              <div
                className={`
                relative flex h-16 w-16 items-center justify-center rounded-2xl
                bg-white/80 dark:bg-stone-900/80 shadow-sm
                ring-1 ring-black/5 dark:ring-white/10
              `}
              >
                <Icon className={`h-8 w-8 ${config.iconClass}`} />
              </div>
            )}
          </div>

          {/* Title */}
          <h3 className="mb-3 text-xl font-semibold tracking-tight text-stone-900 dark:text-stone-100">
            {config.title}
          </h3>

          {/* Description */}
          <p className="mb-4 max-w-md text-sm leading-relaxed text-stone-600 dark:text-stone-400">
            {config.description}
          </p>

          {/* Hint */}
          <p className="max-w-sm text-xs text-stone-500 dark:text-stone-500">
            {config.hint}
          </p>

          {/* Refresh button for pending/failed states */}
          {(status === 'pending' || status === 'failed') && onRefresh && (
            <button
              onClick={onRefresh}
              className="
                mt-6 inline-flex items-center gap-2 rounded-lg
                bg-white dark:bg-stone-800 px-4 py-2
                text-sm font-medium text-stone-700 dark:text-stone-300
                shadow-sm ring-1 ring-stone-200 dark:ring-stone-700
                transition-all duration-200
                hover:bg-stone-50 dark:hover:bg-stone-750
                hover:ring-stone-300 dark:hover:ring-stone-600
                active:scale-[0.98]
              "
            >
              <RefreshCw className="h-4 w-4" />
              Check Status
            </button>
          )}

          {/* Progress indicator for pending/processing */}
          {(status === 'pending' || status === 'processing') && (
            <div className="mt-8 w-full max-w-xs">
              <div className="h-1.5 overflow-hidden rounded-full bg-teal-100 dark:bg-teal-900/40">
                <div
                  className="h-full w-1/3 animate-[shimmer_2s_ease-in-out_infinite] rounded-full bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400 dark:from-teal-500 dark:via-teal-400 dark:to-teal-500"
                  style={{
                    backgroundSize: '200% 100%',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(400%);
          }
        }
      `}</style>
    </div>
  )
}
